import { useCallback, useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { supabase } from "../supabaseClient"
import { useUser } from "../UserContext"
import { cleanGtmTags, fetchTagReference, DEFAULT_TAG_REFERENCE_TABLE } from "../utils/gtm"

export default function GTMDetails() {
  const user = useUser()
  const { websiteId, gtmId } = useParams()
  const [gtmData, setGtmData] = useState([])
  const [eventOwners, setEventOwners] = useState({})
  const [url, setUrl] = useState("")
  const [parsedData, setParsedData] = useState(null)
  const [loadingContents, setLoadingContents] = useState(false)
  const [organizationId, setOrganizationId] = useState(null)
  const [tagReference, setTagReference] = useState(DEFAULT_TAG_REFERENCE_TABLE)
  const navigate = useNavigate()

  const fetchDetails = useCallback(async () => {
    if (!user || !websiteId) return

    let query = supabase
      .from("gtm_instances")
      .select("*")
      .eq("user_id", user.id)
      .eq("website_id", websiteId)
      .order("found_date", { ascending: false })
    if (gtmId) query = query.eq("gtm_id", gtmId)
    const { data } = await query


    if (data) {
      setGtmData(data)
      setUrl(data[0]?.url || "")
      const ownerMap = {}
      data.forEach(row => {
        if (row.gtm_id && row.event_owner) {
          ownerMap[row.gtm_id] = row.event_owner
        }
      })
      setEventOwners(ownerMap)
    }
  }, [gtmId, user, websiteId])

  const fetchOrganization = useCallback(async () => {
    if (!websiteId) return

    const { data } = await supabase
      .from("website_urls")
      .select("organization_id")
      .eq("id", websiteId)
      .single()
    if (data) setOrganizationId(data.organization_id)
  }, [websiteId])

  const fetchReference = useCallback(async () => {
    const ref = await fetchTagReference()
    setTagReference(ref)
  }, [])

  useEffect(() => {
    fetchDetails()
    fetchOrganization()
    if (user) fetchReference()
  }, [fetchDetails, fetchOrganization, fetchReference, user])

  const handleOwnerChange = (gtmId, value) => {
    setEventOwners(prev => ({ ...prev, [gtmId]: value }))
  }

  const saveAllOwners = async () => {
    const updates = Object.entries(eventOwners).map(([gtmId, event_owner]) =>
      supabase
        .from("gtm_instances")
        .update({ event_owner })
        .match({ user_id: user.id, website_id: websiteId, gtm_id: gtmId })
    )

    try {
      await Promise.all(updates)
      alert("Saved all changes!")
      fetchDetails()
    } catch {
      alert("Failed to save some changes.")
    }
  }

  const extractDataSection = (script) => {
    const start = script.indexOf("var data =")
    if (start === -1) return ""
    let idx = script.indexOf("{", start)
    if (idx === -1) return ""
    let depth = 1
    idx += 1
    while (idx < script.length && depth > 0) {
      const ch = script[idx]
      if (ch === "{") depth += 1
      else if (ch === "}") depth -= 1
      idx += 1
    }
    if (script[idx] === ";") idx += 1
    return script.slice(start, idx)
  }

  const combineRules = (tags, triggers, rules) => {
    console.log('combineRules input', { tags, triggers, rules })
    const rows = []
    rules.forEach((rule, idx) => {
      const trigIdx = Array.isArray(rule.if) ? rule.if : []
      const tagIdx = Array.isArray(rule.add) ? rule.add : []
      const triggerNames = trigIdx
        .map((i) => {
          const t = triggers[i]
          return t?.name || t?.filter || t?.type || i
        })
        .join(', ')
      tagIdx.forEach((tagI) => {
        const tag = tags[tagI] || {}
        const row = { rule: idx, triggers: triggerNames, ...tag }
        console.log('combineRules row', row)
        rows.push(row)
      })
    })
    const allKeys = Array.from(new Set(rows.flatMap((r) => Object.keys(r))))
    const result = rows.map((row) => {
      const filled = {}
      allKeys.forEach((k) => {
        filled[k] = row[k] ?? ''
      })
      return filled
    })
    console.log('combineRules result', result)
    return result
  }

const parseGtmData = (snippet) => {
    try {
      const json = snippet
        .replace(/^var data\s*=\s*/, "")
        .replace(/;\s*$/, "")
      const obj = JSON.parse(json)
      const resource = obj.resource || obj
      const tags = resource.tag || resource.tags || []
      const cleanedTags = cleanGtmTags(tags, tagReference)
      const triggers = resource.predicate || resource.predicates || []
      const rules = resource.rule || resource.rules || []
      // log tag-trigger relationships but don't display them in the UI
      combineRules(cleanedTags, triggers, rules)
      return {
        tags: cleanedTags,
        triggers,
        variables: resource.macro || resource.macros || [],
        rules,
      }
    } catch (e) {
      return { tags: [], triggers: [], variables: [], rules: [] }
    }
  }

  const saveContainerContents = async (snippet, parsed) => {
    const data = {
      user_id: user.id,
      website_id: websiteId,
      organization_id: organizationId,
      gtm_id: gtmId,
      contents: snippet,
      tags: parsed.tags,
      triggers: parsed.triggers,
      variables: parsed.variables,
      rules: parsed.rules,
    }

    const { error } = await supabase
      .from("gtm_container_contents")
      .upsert(data, { onConflict: ["user_id", "website_id", "gtm_id"] })

    if (!error) return

    if (error.message.includes("no unique or exclusion constraint")) {
      // Fallback for databases missing the unique index
      const { data: existing } = await supabase
        .from("gtm_container_contents")
        .select("id")
        .match({ user_id: user.id, website_id: websiteId, gtm_id: gtmId })
        .single()

      if (existing) {
        const { error: updateError } = await supabase
          .from("gtm_container_contents")
          .update(data)
          .eq("id", existing.id)
        if (updateError) {
          console.error("Failed to update GTM contents:", updateError.message)
        }
      } else {
        const { error: insertError } = await supabase
          .from("gtm_container_contents")
          .insert(data)
        if (insertError) {
          console.error("Failed to insert GTM contents:", insertError.message)
        }
      }
    } else {
      console.error("Failed to save GTM contents:", error.message)
    }
  }

  const iframeTableStyles = `
    :root {
      color-scheme: dark;
    }

    body {
      background: #0f172a;
      color: #e2e8f0;
      font-family: "Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI",
        sans-serif;
      padding: 1rem;
    }

    h3 {
      font-weight: 600;
      letter-spacing: 0.02em;
      margin-top: 1.5rem;
    }

    .ui-table {
      width: 100%;
      border-collapse: collapse;
      background: rgba(15, 23, 42, 0.72);
      border-radius: 12px;
      overflow: hidden;
    }

    .ui-table th,
    .ui-table td {
      padding: 0.85rem 1.1rem;
      border: 1px solid rgba(148, 163, 184, 0.32);
      text-align: left;
      font-size: 0.95rem;
    }

    .ui-table th {
      text-transform: uppercase;
      letter-spacing: 0.06em;
      font-size: 0.75rem;
      font-weight: 650;
      background: linear-gradient(135deg, rgba(30, 58, 138, 0.95), rgba(59, 130, 246, 0.45));
      color: #f8fafc;
    }

    .ui-table tbody tr:nth-child(even) td {
      background: rgba(59, 130, 246, 0.08);
    }
  `

  const toHtmlTable = (rows) => {
    if (!rows || rows.length === 0) return "<p>No data</p>"
    const keys = Array.from(new Set(rows.flatMap((r) => Object.keys(r))))
    let html =
      '<table class="ui-table" style="width:100%;border-collapse:collapse"><thead><tr>'
    html += keys.map((k) => `<th>${escapeHtml(k)}</th>`).join('')
    html += '</tr></thead><tbody>'
    rows.forEach((row) => {
      html +=
        '<tr>' +
        keys
          .map((k) => `<td>${escapeHtml(String(row[k] ?? ''))}</td>`)
          .join('') +
        '</tr>'
    })
    html += '</tbody></table>'
    return html
  }

  const buildIframeHtml = (data) => {
    if (!data) return ''
    const parts = []
    if (data.tags && data.tags.length) {
      parts.push(`<h3>Tags</h3>${toHtmlTable(data.tags)}`)
    }
    if (data.triggers && data.triggers.length) {
      parts.push(`<h3>Triggers</h3>${toHtmlTable(data.triggers)}`)
    }
    if (data.variables && data.variables.length) {
      parts.push(`<h3>Variables</h3>${toHtmlTable(data.variables)}`)
    }
    return `<html><head><style>${iframeTableStyles}</style></head><body>${parts.join(
      ''
    )}</body></html>`
  }

  const escapeHtml = (str) =>
    str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")

  const inspectContents = async () => {
    if (!gtmId) return
    setLoadingContents(true)
    try {
      const res = await fetch(
        `https://www.googletagmanager.com/gtm.js?id=${gtmId}`
      )
      const text = await res.text()
      const snippet = extractDataSection(text)
      const parsed = parseGtmData(snippet)
      setParsedData(parsed)
      await saveContainerContents(snippet, parsed)
    } catch (e) {
      setParsedData(null)
    }
    setLoadingContents(false)
  }

  return (
    <div className="page-container">
      <h2>GTM Details for {url}</h2>
      <button onClick={() => navigate("/website-audit/gtm-management")}>Back</button>
      <table className="ui-table" style={{ width: "100%", marginTop: "1rem" }}>
        <thead>
          <tr>
            <th>GTM ID</th>
            <th>Found Date</th>
            <th>Removed Date</th>
            <th>Found Method</th>
            <th>Event Owner</th>
          </tr>
        </thead>
        <tbody>
          {gtmData.map((row) => (
            <tr key={row.id} style={{ opacity: row.removed_date ? 0.5 : 1 }}>
              <td>{row.gtm_id}</td>
              <td>{row.found_date?.split("T")[0]}</td>
              <td>{row.removed_date?.split("T")[0] || ""}</td>
              <td>{row.found_method}</td>
              <td>
                <input
                  type="text"
                  value={eventOwners[row.gtm_id] || ""}
                  onChange={(e) => handleOwnerChange(row.gtm_id, e.target.value)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button style={{ marginTop: "1rem" }} onClick={saveAllOwners}>
        Save All Changes
      </button>
      {gtmId && (
        <div style={{ marginTop: "1rem" }}>
          <button onClick={inspectContents} disabled={loadingContents}>
            {loadingContents ? "Loading..." : "Inspect Contents"}
          </button>
          {parsedData && (
            <iframe
              title="GTM Contents"
              style={{
                width: "100%",
                height: "400px",
                border: "1px solid #ccc",
                marginTop: "0.5rem",
              }}
              srcDoc={buildIframeHtml(parsedData)}
            />
          )}
        </div>
      )}
    </div>
  )
}
